/**
 * Server state for the registry: the doctor list with readiness, one doctor
 * with readiness for every report type, and the writes. Every write carries
 * the version the page read; a 409 conflict is shown inline where the person
 * is typing; a 409 not decided is the ADR-0001 seam and is shown where the
 * action sits; a 422 shows the server's reason. Success replaces the cache
 * with the returned view so readiness re-renders from the server's answer.
 * Uploads go through XMLHttpRequest so their progress is real.
 */

import type { ReportTypeKind } from '@beam/core/status';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, readActingAs, reasonOf } from '@/app/api';
import type { ClinicRecord, DoctorListRow, DoctorPatch, DoctorView, FormCode, FormRecord, PracticeImageKind, PracticePatch, PracticeRecord, ReferrerRecord } from '@/app/types';

export const doctorsKey = ['registry', 'doctors'] as const;
export const doctorKey = (slug: string) => ['registry', 'doctors', slug] as const;
export const practiceKey = ['registry', 'practice'] as const;

const base = (slug: string) => `/api/registry/doctors/${encodeURIComponent(slug)}`;
export const signatureUrl = (slug: string, version: number) => `${base(slug)}/signature?v=${version}`;
export const photoUrl = (slug: string, version: number) => `${base(slug)}/photo?v=${version}`;
export const practiceImageUrl = (kind: PracticeImageKind, version: number) => `/api/registry/practice/${kind}?v=${version}`;

export function usePractice() {
  return useQuery({ queryKey: practiceKey, queryFn: () => api<PracticeRecord>('/api/registry/practice'), retry: (count) => count < 1, staleTime: 10_000 });
}

/** The practice's name and contact details; the version read travels with the patch (absent before the first write). */
export function useUpdatePractice() {
  const client = useQueryClient();
  return useMutation({
    mutationFn: (vars: { patch: PracticePatch; version: number | undefined }) => api<PracticeRecord>('/api/registry/practice', { method: 'PUT', json: { ...(vars.version === undefined ? {} : { version: vars.version }), ...vars.patch } }),
    onSuccess: (record) => { client.setQueryData(practiceKey, record); },
  });
}

export const formsKey = ['registry', 'forms'] as const;
export function useForms() {
  return useQuery({ queryKey: formsKey, queryFn: () => api<FormRecord[]>('/api/registry/forms'), retry: (count) => count < 1, staleTime: 10_000 });
}

/** An official form as raw .docx bytes; the file name travels in a header. Resolves with the form record the server returns. */
export function uploadForm(code: FormCode, file: File, onProgress: (percent: number) => void, signal?: AbortSignal): Promise<FormRecord> {
  return putBytes<FormRecord>(`/api/registry/forms/${code}`, file, { 'x-file-name': encodeURIComponent(file.name) }, onProgress, signal);
}

export function useDoctors() {
  return useQuery({ queryKey: doctorsKey, queryFn: () => api<DoctorListRow[]>('/api/registry/doctors'), retry: (count) => count < 1, staleTime: 10_000 });
}

export function useClinics() {
  return useQuery({ queryKey: ['registry', 'clinics'], queryFn: () => api<ClinicRecord[]>('/api/registry/clinics'), staleTime: 60_000 });
}

export function useReferrers() {
  return useQuery({ queryKey: ['registry', 'referrers'], queryFn: () => api<ReferrerRecord[]>('/api/registry/referrers'), staleTime: 300_000 });
}

export function useDoctor(slug: string) {
  return useQuery({
    queryKey: doctorKey(slug),
    queryFn: () => api<DoctorView>(base(slug)),
    retry: (count, err) => !(err instanceof ApiError && err.status === 404) && count < 1,
    staleTime: 10_000,
  });
}

/** What a refused write means to the page: a stale version, the ADR seam, a field problem, or something else. */
export type RefusalKind = 'conflict' | 'not_decided' | 'invalid' | 'other';
export function refusalOf(err: unknown): { kind: RefusalKind; reason: string | null } {
  if (err instanceof ApiError) {
    const body = (err.body ?? {}) as { error?: string };
    if (err.status === 409 && body.error === 'not_decided') return { kind: 'not_decided', reason: reasonOf(err) };
    if (err.status === 409) return { kind: 'conflict', reason: reasonOf(err) };
    if (err.status === 422 || err.status === 415) return { kind: 'invalid', reason: reasonOf(err) };
  }
  return { kind: 'other', reason: null };
}

/** The one place a returned view lands: the doctor's cache and the list's invalidation. */
export function useSettleDoctor(slug: string) {
  const client = useQueryClient();
  return {
    settle: (view: DoctorView) => {
      client.setQueryData<DoctorView>(doctorKey(slug), view);
      void client.invalidateQueries({ queryKey: doctorsKey });
    },
    reload: () => client.invalidateQueries({ queryKey: doctorKey(slug) }),
  };
}

export type UpdateVars = { patch: DoctorPatch; version: number };

/** One patch per panel save; the caller decides what the answer means for its form. */
export function useUpdateDoctor(slug: string) {
  const { settle } = useSettleDoctor(slug);
  return useMutation({
    mutationFn: (vars: UpdateVars) => api<DoctorView>(base(slug), { method: 'PUT', json: { version: vars.version, ...vars.patch } }),
    onSuccess: (view) => settle(view),
  });
}

/** The ADR-0001 seam: the server answers 409 not decided; the page shows the reason where the button sits. */
export type UploadKind = { kind: 'template'; reportType: ReportTypeKind } | { kind: 'signature' } | { kind: 'photo' };

export class UploadRefused extends Error {
  readonly status: number;
  readonly reason: string | null;
  constructor(status: number, reason: string | null) {
    super(reason ?? `upload refused with ${status}`);
    this.name = 'UploadRefused';
    this.status = status;
    this.reason = reason;
  }
}

/** A raw-byte PUT with progress; the file name travels in a header because the body is the file. Resolves with the doctor view the server returns. */
export function uploadAsset(slug: string, target: UploadKind, file: File, onProgress: (percent: number) => void, signal?: AbortSignal): Promise<DoctorView> {
  const path = target.kind === 'template' ? `${base(slug)}/templates/${target.reportType}` : `${base(slug)}/${target.kind}`;
  return putBytes<DoctorView>(path, file, target.kind === 'template' ? { 'x-file-name': encodeURIComponent(file.name) } : {}, onProgress, signal);
}

/** The practice's letterhead or footer strip; resolves with the practice record the server returns. */
export function uploadPracticeImage(kind: PracticeImageKind, file: File, onProgress: (percent: number) => void, signal?: AbortSignal): Promise<PracticeRecord> {
  return putBytes<PracticeRecord>(`/api/registry/practice/${kind}`, file, {}, onProgress, signal);
}

/** Uploads go through XMLHttpRequest so their progress is real; the server's JSON answer is the result. */
function putBytes<T>(path: string, file: File, headers: Record<string, string>, onProgress: (percent: number) => void, signal?: AbortSignal): Promise<T> {
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open('PUT', path);
    xhr.setRequestHeader('content-type', file.type || 'application/octet-stream');
    xhr.setRequestHeader('accept', 'application/json');
    for (const [name, value] of Object.entries(headers)) xhr.setRequestHeader(name, value);
    const actingAs = readActingAs();
    if (actingAs) xhr.setRequestHeader('x-beam-acting-as', actingAs);
    xhr.upload.onprogress = (e) => { if (e.lengthComputable) onProgress(Math.round((e.loaded / e.total) * 100)); };
    xhr.onerror = () => reject(new UploadRefused(0, null));
    xhr.onabort = () => reject(new DOMException('aborted', 'AbortError'));
    xhr.onload = () => {
      let body: unknown = null;
      try { body = JSON.parse(xhr.responseText); } catch { body = null; }
      if (xhr.status >= 200 && xhr.status < 300) resolve(body as T);
      else {
        const reason = body && typeof body === 'object' && 'reason' in body && typeof (body as { reason?: unknown }).reason === 'string' ? (body as { reason: string }).reason : null;
        reject(new UploadRefused(xhr.status, reason));
      }
    };
    signal?.addEventListener('abort', () => xhr.abort());
    xhr.send(file);
  });
}
